import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

// Alert 组件：参考给定样式，提供 info/danger/warning/success 四种变体。
// 设计理念：
// - 严格的输入校验：无效入参直接抛出错误，交由 ErrorBoundary 捕获；不做隐式 fallback。
// - 可访问性：role="alert"，提供 sr-only 文案；图标 aria-hidden。
// - 安全：仅渲染纯文本与受控 children，不拼接 HTML；禁止在组件内部进行不必要的数据修复。

const VARIANT_STYLES = {
  info: {
    container: 'text-blue-800 bg-blue-50 dark:bg-gray-800 dark:text-blue-400',
    srOnly: 'Info',
  },
  danger: {
    container: 'text-red-800 bg-red-50 dark:bg-gray-800 dark:text-red-400',
    srOnly: 'Danger',
  },
  warning: {
    container: 'text-yellow-800 bg-yellow-50 dark:bg-gray-800 dark:text-yellow-400',
    srOnly: 'Warning',
  },
  success: {
    container: 'text-green-800 bg-green-50 dark:bg-gray-800 dark:text-green-400',
    srOnly: 'Success',
  },
} as const

type AlertVariant = keyof typeof VARIANT_STYLES

const Icon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
  </svg>
)

const assertInvariant = (condition: boolean, message: string) => {
  if (!condition) throw new Error(message)
}

export const Alert = ({
  variant = 'info',
  title,
  items,
  children,
  className = '',
  role = 'alert',
}: {
  variant?: AlertVariant
  title?: ReactNode
  items?: string[]
  children?: ReactNode
  className?: string
  role?: ComponentProps<'div'>['role']
}) => {
  const { t } = useTranslation()
  // 严格参数校验：不做兜底
  assertInvariant(variant in VARIANT_STYLES, `Alert: invalid variant '${variant}'.`)
  if (items !== undefined) {
    assertInvariant(Array.isArray(items), 'Alert: items must be an array of strings.')
    assertInvariant(items.every((v: string) => typeof v === 'string'), 'Alert: items must be strings.')
  }
  const hasContent = Boolean(title) || Boolean(children) || (Array.isArray(items) && items.length > 0)
  assertInvariant(hasContent, 'Alert: content is empty. Provide title, children, or items.')

  const varStyle = VARIANT_STYLES[variant]

  return (
    <div
      className={`flex p-4 mb-4 text-sm rounded-lg ${varStyle.container} ${className}`}
      role={role}
    >
      <Icon className="shrink-0 inline w-4 h-4 me-3 mt-[2px]" />
      <span className="sr-only">{t(varStyle.srOnly)}</span>
      <div>
        {title ? <span className="font-medium">{title}</span> : null}
        {Array.isArray(items) && items.length > 0 ? (
          <ul className="mt-1.5 list-disc list-inside">
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        ) : null}
        {children}
      </div>
    </div>
  )
}

// AlertBoundary：用于包裹 Alert（或任意子树），防止渲染出错影响上层树。
// 失败时渲染一个简洁的危险态块，不泄露堆栈细节（以免在生产环境暴露实现信息）。
export const AlertBoundary = ({ children, fallbackTitle }: { children: ReactNode; fallbackTitle?: string }) => {
  const { t } = useTranslation()
  const resolvedFallbackTitle = fallbackTitle || t('组件渲染失败')
  return (
    <ReactErrorBoundary
      fallbackRender={() => (
        <div
          className="flex p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400"
          role="alert"
        >
          <Icon className="shrink-0 inline w-4 h-4 me-3 mt-[2px]" />
          <span className="sr-only">{t('Danger')}</span>
          <div>
            <span className="font-medium">{resolvedFallbackTitle}</span>
          </div>
        </div>
      )}
    >
      {children}
    </ReactErrorBoundary>
  )
}

// 便捷封装：Alert.WithBoundary 组合使用，减少业务层样板代码。
Alert.WithBoundary = (props: ComponentProps<typeof Alert>) => (
  <AlertBoundary fallbackTitle={typeof props.title === 'string' ? props.title : undefined}>
    <Alert {...props} />
  </AlertBoundary>
)

Alert.displayName = 'Alert'
AlertBoundary.displayName = 'AlertBoundary'
